import { ReactNode, useMemo, useState } from "react"
import { MinimalBusiness } from "@/schema"
import { InlineSelect } from "@ui/forms/inline"
import { type LucideIcon } from "lucide-react"

import { useFetchBusinesses } from "@/hooks/api/businesses"

export interface InlineBusinessSelectProps {
  label?: ReactNode
  LabelIcon?: LucideIcon
  selectedBusiness?: MinimalBusiness | null
  setSelectedBusinessId: (id: string | null) => void
  onSave?: (id: string | null) => void | Promise<void>
  isDisabled?: boolean
  fixedLabelWidth?: string
  hideLabel?: boolean
  size?: "sm" | "base" | "lg"
}

export function InlineBusinessSelect({
  label,
  LabelIcon,
  selectedBusiness,
  setSelectedBusinessId,
  onSave,
  isDisabled,
  fixedLabelWidth,
  hideLabel,
  size,
}: InlineBusinessSelectProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const { data: businessesResponse, isLoading } = useFetchBusinesses(searchQuery)

  const options = useMemo(() => {
    const selectedOption =
      selectedBusiness && selectedBusiness.id
        ? {
            value: selectedBusiness.id,
            label: selectedBusiness.name || "Unnamed business",
          }
        : null

    const otherOptions =
      businessesResponse?.businesses
        ?.filter((business) => business.id !== selectedBusiness?.id)
        .map((business) => ({
          value: business.id,
          label: business.name || "Unnamed business",
        })) || []

    return selectedOption ? [selectedOption, ...otherOptions] : otherOptions
  }, [businessesResponse?.businesses, selectedBusiness])

  const handleValueChange = async (value: string | null) => {
    setSelectedBusinessId(value)
    if (onSave) {
      await onSave(value)
    }
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
  }

  return (
    <InlineSelect
      label={label}
      LabelIcon={LabelIcon}
      defaultValue={selectedBusiness?.id}
      placeholder={isLoading ? "Loading..." : "Select business..."}
      options={options}
      disabled={isLoading || isDisabled}
      onSave={handleValueChange}
      onSearch={handleSearch}
      isSearching={isLoading}
      fixedLabelWidth={fixedLabelWidth}
      hideLoader
      hideLabel={hideLabel}
      size={size}
    />
  )
}
